import React, { useContext, useEffect, useState } from "react";
import { getImagesRequest } from "../../../api/LinksApi";
import ResultsLayout from "./results-layout";
import CoreLayout from "../../../layouts/core-layout";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCart } from "react-use-cart";
import LoadingResults from "./elements/LoadingResults";
import { useQuery } from "react-query";
import ButtonRemove from "../components/ButtonRemove";
import ButtonAdd from "../components/ButtonAdd";
import { createBasicDiagnosisRequestEvent, getDiagnosisByUIIDRequest } from "../../../api/DiagnosisApi";
import { BankManagerAccounContext } from "../../../lib/bank-manager/BankManagerAccount";
import { ProductActionHandler } from "../../../lib/product-action-handler/ProductActionHandler";
import { getProductActions, ProductActionStatus } from "../../../api/ProductManagerActionApi";
import { ModalConfirm, PriceDisplay, WarningQuotas } from "../../../lib/product-manager/ProductManager";
import i18n from "../../../i18n";
import { AttributeGroup, PackageAttribute, Product, ProductAttribute } from "../../../lib/product-manager/types";
import { ProductAttributeGroupManager } from "../../../lib/product-manager/product-attribute-group";
import { getPackageOrProduct } from "../../../lib/product-manager/product-manager.service";

export default function ImagesResults() {
  const { diagUUID } = useParams();
  const { t } = useTranslation(); // Multi Language
  const [diagnosis, setDiagnosis] = useState<{
    keyword: string
    negativeKeyword: string
  } | null>(null)

  const [refreshCount, setRefreshCount] = useState<number>(0)
  const { data, isLoading } = useQuery(["images", refreshCount, diagUUID], async () =>
    getImagesRequest(diagUUID).then((data) => {
      return data;
    }),
  );

  useEffect(() => {
    getDiagnosisByUIIDRequest(diagUUID).then(function (res: any) {
      setDiagnosis(res.data.data)
    });
  }, [diagUUID, refreshCount]);

  function getActionMetadata() {
    return {}
  }

  const { account, refresh: refreshAccount } = useContext(BankManagerAccounContext)
  const [pendingAction, setPendingAction] = useState<{
    checkoutId: number;
    pendingActionCount: number;
  } | null
  >(null)
  const navigate = useNavigate()


  function handleIsActionPending(params: {
    checkoutId: number;
    pendingActionCount: number;
  }) {
    setPendingAction(params)
  }

  const [product, setProduct] = useState<(Product | null)>(null)
  const [selectedOptions, setSelectedOptions] = useState<{ groupId: number, id: number }[]>([])
  const [modalConfirmToggled, setModalConfirmToggled] = useState<boolean>(false)

  useEffect(() => {
    getPackageOrProduct('google-image', 'Product').then((data) => data.results[0])
      .then((product: Product) => {
        setProduct(product)
        setSelectedOptions(
          product.attributeGroups.reduce((array, attributeGroup) => {
            array = [...array, ...attributeGroup.attributes.filter((attribute) => attribute.isDefault).map((attribute) => ({
              ...attribute,
              group: { id: attributeGroup.id } as AttributeGroup<PackageAttribute | ProductAttribute>
            }))]
            return array
          }, [] as ProductAttribute[])
            .map((attribute: ProductAttribute) => ({
              groupId: attribute?.group?.id as unknown as number,
              id: attribute.id as unknown as number
            }))
        )
      }
      )
  }, [refreshCount])

  if (isLoading) {
    return (
      <CoreLayout template="main">
        <ResultsLayout
          description={t("core:pages.osculteo.results.section.suggest.text")}
        >
          <div className="text-center mt-20 mb-20">
            <LoadingResults />
          </div>
        </ResultsLayout>
      </CoreLayout>
    );
  }

  if (data?.length === 0) {
    return (
      <CoreLayout template="main">
        <ResultsLayout
          description={t("core:pages.osculteo.results.section.suggest.text")}
        >

          <ProductActionHandler
            pendingActionCount={pendingAction?.pendingActionCount}
            checkoutId={pendingAction?.checkoutId}
            getActions={
              (checkoutId: number) =>
                getProductActions({ status: [ProductActionStatus.SUCCESS], checkoutId }).then((response) => {
                  return response.data.data.results
                })
            }
            onTargetedActionExists={(targetedActions) => {
              refreshAccount()
              setPendingAction(null)
              setRefreshCount((state) => state + 1)
              setModalConfirmToggled(false)
            }}
          >
            <div className="pb-20">


              <div className="form w-lg-500px shadow-sm p-8 mx-auto">

                {product?.prices?.[0]?.value ? <div className="mb-10 d-flex align-items-center justify-content-end">
                  <PriceDisplay
                    currentLanguage={i18n.language || 'fr'}
                    value={product?.prices?.[0]?.value || 0}
                    creditSystemEnabled={process.env.REACT_APP_CREDIT_SYSTEM_ENABLED === '1'}
                  />
                </div> : null}


                <div className="text-center mb-10">
                  <h1 className="text-dark mb-3">
                    {t("core:pages.osculteo.DiagnosisImages.title")}
                  </h1>
                </div>
                <div className="mb-10">
                  {t("core:pages.osculteo.DiagnosisImages.text")}
                </div>


                <div className="fv-row mb-10">
                  {product && product.attributeGroups.length ?
                    <ProductAttributeGroupManager
                      currentLanguage={i18n.language || 'fr'}
                      setSelectedOptions={setSelectedOptions}
                      selectedOptions={selectedOptions}
                      groups={product.attributeGroups}
                      creditSystemEnabled={process.env.REACT_APP_CREDIT_SYSTEM_ENABLED === '1'}
                    /> : null

                  }
                </div>

                <div className="text-center mt-4">
                  <button
                    className={`btn btn-lg ${!product || !product.isAvailable ? 'btn-secondary' : 'btn-primary'} w-100 mb-5`}
                    disabled={!product || !product.isAvailable}
                    onClick={() => setModalConfirmToggled(true)}
                  >
                    {t(
                      "core:pages.osculteo.DiagnosisWithNegativeKeyword.forms.button"
                    )}

                  </button>
                </div>

                {!product?.isAvailable ? <WarningQuotas products={[product as Product]} currentLanguage={i18n.language || 'fr'} /> : null}

              </div>

            </div>


            <ModalConfirm
              diagUUID={diagUUID}
              creditSystemEnabled={process.env.REACT_APP_CREDIT_SYSTEM_ENABLED === '1'}
              createNewDiagnosis={(params) => createBasicDiagnosisRequestEvent(params).then(({ data }) => data.data.uuid)}
              loadingImageBaneerSrc={`${process.env.REACT_APP_CDN_PLATFORMS}/slider-1.jpg`}
              currentLanguage={i18n.language as 'fr' | 'en'}
              userBalance={process.env.REACT_APP_CREDIT_SYSTEM_ENABLED === "1" ? account?.balance || 0 : null}
              getActionMetadata={getActionMetadata}
              isActionPending={pendingAction ? true : false}
              setIsActionPending={handleIsActionPending}
              selectedItems={product && selectedOptions ? [
                { ...product, selectedProductAttributesIds: selectedOptions.map((selectedOption) => selectedOption.id) }
              ] : []}
              setShow={setModalConfirmToggled}
              show={modalConfirmToggled}
            />

          </ProductActionHandler>

        </ResultsLayout>
      </CoreLayout>
    );
  }

  return (
    <CoreLayout template="main">
      <ResultsLayout
        description={t("core:pages.osculteo.results.section.images.text")}
      >
        <div className="row">
          {data?.map((item: any) => (

            <ImageComponent key={item.id} item={item} />
          ))}
        </div>
      </ResultsLayout>
    </CoreLayout>
  );
}
const ImageComponent = (props: any) => {
  const { diagUUID } = useParams();
  const { inCart } = useCart();


  return (
    <>
      <div className="col-md-3 mb-10" key={props.item.id}>

        <div className={`card overlay overflow-hidden ${inCart(props.item.id) ? "imageCardinCart " : ""}`}>
          <div className="card-body p-0">
            <div className="overlay-wrapper">
              <div className={`${inCart(props.item.id) ? "imageCardinCartBackground" : ""}`}>
                <div className={`${inCart(props.item.id) ? "blurImage" : ""}`}>
                  <div
                    className={`imgResults ${inCart(props.item.id) ? "" : ""}`}
                    style={{
                      backgroundImage: `url(${props.item.src})`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="overlay-layer bg-dark bg-opacity-25">
              {inCart(props.item.id) ? (
                <ButtonRemove itemId={props.item.id} />
              ) : (
                <ButtonAdd item={props.item} diag={diagUUID} />
              )}
            </div>
          </div>
        </div>

      </div >
      {/* <ChangeSentimentModalImages
        showHelpModal={showHelpModal}
        handleCloseHelpModal={handleCloseHelpModal}
        uuid={props.item.uuid}
        tonality={props.item.tonality}
      /> */}
    </>
  );
};
